import api from './api';

const HOTELS_CACHE_KEY = "guestified-hotels-cache";
const HOTELS_CACHE_TTL = 10 * 60 * 1000;
const APALEO_PROPERTY_CODE_PATTERN = /^[A-Z0-9]{3,10}$/;

export const JOURNEY_TOKEN_STORAGE_KEY = "guest-access-token";
export const JOURNEY_API_STORAGE_KEY = "guest-journey-api";

let currentJourneyToken = "";

export const setCurrentJourneyToken = (guestAccessToken) => {
  currentJourneyToken = String(guestAccessToken || "").trim();
};

const getCurrentJourneyToken = () => currentJourneyToken;
const getStoredJourneyToken = () => localStorage.getItem(JOURNEY_TOKEN_STORAGE_KEY);

const getStoredJourneyApi = () => {
  try {
    return JSON.parse(localStorage.getItem(JOURNEY_API_STORAGE_KEY) || "{}");
  } catch {
    return {};
  }
};

const normalizeApiPath = (path) => {
  if (!path) return "";

  const pathString = String(path).trim();
  if (/^https?:\/\//i.test(pathString)) {
    try {
      const url = new URL(pathString);
      return `${url.pathname}${url.search}${url.hash}`.replace(/^\/api(?=\/)/, "");
    } catch {
      return pathString;
    }
  }

  return pathString.replace(/^\/api(?=\/)/, "");
};

const journeyPath = (key, fallback) => normalizeApiPath(getStoredJourneyApi()?.[key] || fallback);

export const fetchJourney = async (guestAccessToken) => {
  const response = await api.get(`/journey/${guestAccessToken}/`);
  return response.data;
};

export const fetchBookingJourney = async (bookingAccessToken) => {
  const response = await api.get(`/booking-journey/${encodeURIComponent(bookingAccessToken)}/`);
  return response.data;
};

export const updateBookingJourneyLanguage = async (bookingAccessToken, language, endpoint = "") => {
  if (!bookingAccessToken || !language) {
    throw new Error("Booking access token and language are required.");
  }

  const path = endpoint
    ? normalizeApiPath(endpoint)
    : `/booking-journey/${encodeURIComponent(bookingAccessToken)}/`;

  const response = await api.patch(path, { language });
  return response.data;
};

export const assignBookingRoomGuest = async (bookingAccessToken, reservationId, data, endpoint = "") => {
  if (!bookingAccessToken || !reservationId) {
    throw new Error("Booking access token and reservation id are required.");
  }

  const path = endpoint
    ? normalizeApiPath(endpoint)
    : `/booking-journey/${encodeURIComponent(bookingAccessToken)}/reservations/${encodeURIComponent(reservationId)}/room-guest/`;

  const response = await api.post(path, data);
  return response.data;
};

const normalizeDoorAccessPayload = (data = {}) => ({
  ...data,
  roomAccess: data.roomAccess || data.room_access || null,
  doorAccess: data.doorAccess || data.door_access || null,
});

const getDoorAccessPath = () => {
  const token = getStoredJourneyToken();
  if (!token) {
    throw new Error("Guest access token is missing.");
  }

  return journeyPath("door_access", `/journey/${token}/door-access/`);
};

export const fetchDoorAccess = async () => {
  const response = await api.get(getDoorAccessPath());
  return normalizeDoorAccessPayload(response.data);
};

export const openDoorAccess = async (door) => {
  const doorKey = door?.doorKey || door?.door_key;

  if (!doorKey) {
    throw new Error("Door key is missing.");
  }

  const response = await api.post(getDoorAccessPath(), { doorKey });
  return normalizeDoorAccessPayload(response.data);
};

export const saveJourneyLanguage = async (language) => {
  const token = getStoredJourneyToken();
  if (!token) return null;

  const response = await api.patch(journeyPath("check_in", `/journey/${token}/check-in/`), { language });
  return response.data;
};

export const fetchHotels = async () => {
  const cached = sessionStorage.getItem(HOTELS_CACHE_KEY);

  if (cached) {
    try {
      const { timestamp, hotels } = JSON.parse(cached);
      if (Array.isArray(hotels) && Date.now() - timestamp < HOTELS_CACHE_TTL) {
        return hotels;
      }
    } catch {
      sessionStorage.removeItem(HOTELS_CACHE_KEY);
    }
  }

  const response = await api.get("/hotels/");
  const hotels = response.data || [];
  sessionStorage.setItem(HOTELS_CACHE_KEY, JSON.stringify({
    timestamp: Date.now(),
    hotels,
  }));
  return hotels;
};

export const resolveApaleoProperty = async (hotelParam) => {
  const normalizedParam = String(hotelParam || "").trim();
  const upperParam = normalizedParam.toUpperCase();

  if (APALEO_PROPERTY_CODE_PATTERN.test(upperParam)) {
    return {
      propertyId: upperParam,
      hotel: null,
    };
  }

  const hotels = await fetchHotels();
  const matchedHotel = hotels.find((hotel) =>
    hotel.name?.toLowerCase() === normalizedParam.toLowerCase()
  );

  if (!matchedHotel) {
    throw new Error(`Unknown property "${normalizedParam}".`);
  }

  if (!matchedHotel.apaleo_property_id) {
    throw new Error(`Property "${matchedHotel.name}" is not connected to Apaleo.`);
  }

  return {
    propertyId: matchedHotel.apaleo_property_id,
    hotel: matchedHotel,
  };
};

export const fetchReservationFromPms = async (reservationCode, propertyId) => {
  if (!propertyId || !APALEO_PROPERTY_CODE_PATTERN.test(String(propertyId).toUpperCase())) {
    throw new Error("A valid Apaleo property code is required to sync the reservation.");
  }

  const response = await api.post("/get-reservation/", {
    reservation_id: reservationCode,
    property_id: String(propertyId).toUpperCase(),
  });
  return response.data;
};

export const fetchReservation = async (reservationCode) => {
  const response = await api.get(`/reservation/${reservationCode}/`);
  return response.data;
};

export const updateGuestProfile = async (reservationCode, data) => {
  if (getStoredJourneyToken() && Object.prototype.hasOwnProperty.call(data || {}, "language")) {
    return saveJourneyLanguage(data.language);
  }

  return api.patch(`/guests/${reservationCode}/update-guest/`, data);
};

export const sendEmail = async (emailPayload) => {
  const response = await api.post("/send-email/", emailPayload);
  return response.data;
}

export const updateReservationState = async (state, guestAccessToken = getCurrentJourneyToken()) => {
  if (!state) {
    throw new Error("Reservation state is required.");
  }
  if (!guestAccessToken) {
    throw new Error("Guest journey token is missing.");
  }

  const response = await api.patch(normalizeApiPath(`/journey/${guestAccessToken}/state/`), { state });
  return response.data;
};

export const updateReservation = async (_reservationCode, data) => {
  const keys = Object.keys(data || {});
  if (keys.length === 1 && Object.prototype.hasOwnProperty.call(data, "state")) {
    return updateReservationState(data.state);
  }

  throw new Error("Public reservation mutations are disabled for guest journeys.");
};

export const fetchGuestByFilter = async (query) => {
  const response = await api.get(`/guests-filter/?${query}`);
  return response.data;
};

export const replaceGuests = async (payload) => {
  const response = await api.post("/replace-guests/", payload);
  return response.data;
}

export const awardLoyalty = async (reservationCode) => {
  const response = await api.post(`/reservation/${reservationCode}/award-loyalty/`, );
  return response.data;
}

export const fetchCheckInFlow = async () => {
  const response = await api.get(`${ownerCheckInPath()}/`);
  return response.data;
};

const ownerCheckInPath = () => {
  const token = getStoredJourneyToken();
  if (!token) throw new Error("Guest journey token is missing.");
  return journeyPath("check_in", `/journey/${encodeURIComponent(token)}/check-in/`).replace(/\/$/, "");
};

export const fetchCheckInServiceOffers = async () => {
  const response = await api.get(`${ownerCheckInPath()}/service-offers/`);
  return response.data;
};

export const bookCheckInService = async (data) => {
  const response = await api.post(`${ownerCheckInPath()}/services/book/`, data);
  return response.data;
};

export const removeCheckInService = async (data) => {
  const response = await api.post(`${ownerCheckInPath()}/services/remove/`, data);
  return response.data;
};

export const saveRegistrationCard = async (guestId, data, files = {}) => {
  const endpoint = `${ownerCheckInPath()}/guests/${encodeURIComponent(guestId)}/`;
  const hasFiles = Object.values(files).some(Boolean);

  if (hasFiles) {
    const formData = new FormData();
    Object.entries(data).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        formData.append(key, value);
      }
    });
    Object.entries(files).forEach(([key, file]) => {
      if (file) {
        formData.append(key, file);
      }
    });

    const response = await api.patch(normalizeApiPath(endpoint), formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return response.data;
  }

  const response = await api.patch(normalizeApiPath(endpoint), data);
  return response.data;
};

export const completeCheckIn = async () => {
  const response = await api.post(`${ownerCheckInPath()}/complete/`, {
    dry_run: false,
  });
  return response.data;
};

const ownerCheckOutPath = () => {
  const token = getStoredJourneyToken();
  if (!token) throw new Error("Guest journey token is missing.");
  return journeyPath("check_out", `/journey/${encodeURIComponent(token)}/check-out/`).replace(/\/$/, "");
};

export const fetchCheckInPaymentState = async () => {
  const response = await api.get(`${ownerCheckOutPath()}/`);
  return response.data;
};

export const createCheckInPaymentLink = async () => {
  const response = await api.post(`${ownerCheckOutPath()}/payment-link/`);
  return response.data;
};

export const redirectToPaymentLink = (url) => {
  window.location.assign(url);
};

export const fetchCheckOutFlow = async (reservationCode) => {
  const token = getStoredJourneyToken();
  if (token) {
    const response = await api.get(journeyPath("check_out", `/journey/${token}/check-out/`));
    return response.data;
  }

  const response = await api.get(`/check-out/${reservationCode}/`);
  return response.data;
};

export const saveCheckoutSession = async (reservationCode, data) => {
  const token = getStoredJourneyToken();
  const checkOutBasePath = journeyPath("check_out", `/journey/${token}/check-out/`).replace(/\/$/, "");
  const endpoint = token
    ? `${checkOutBasePath}/session/`
    : `/check-out/${reservationCode}/session/`;
  const response = await api.patch(normalizeApiPath(endpoint), data);
  return response.data;
};

export const completeCheckOut = async (reservationCode, action, dryRun = true) => {
  const token = getStoredJourneyToken();
  const checkOutBasePath = journeyPath("check_out", `/journey/${token}/check-out/`).replace(/\/$/, "");
  const endpoint = token
    ? `${checkOutBasePath}/complete/`
    : `/check-out/${reservationCode}/complete/`;
  const response = await api.post(normalizeApiPath(endpoint), {
    action,
    dry_run: dryRun,
  });
  return response.data;
};

export const createCheckoutPaymentLink = async (reservationCode) => {
  const token = getStoredJourneyToken();
  const checkOutBasePath = journeyPath("check_out", `/journey/${token}/check-out/`).replace(/\/$/, "");
  const endpoint = token
    ? `${checkOutBasePath}/payment-link/`
    : `/check-out/${reservationCode}/payment-link/`;
  const response = await api.post(normalizeApiPath(endpoint));
  return response.data;
};

export const downloadCheckoutInvoicePdf = async (reservationCode) => {
  const response = await api.get(`/check-out/${reservationCode}/invoice-pdf/`, {
    responseType: "blob",
  });
  return response.data;
};

export const fetchDirectory = async (propertyCode) => {
  if (!propertyCode) {
    throw new Error("Property code is required.");
  }

  const response = await api.get(`/directory/${encodeURIComponent(propertyCode)}/`);
  return response.data;
};

export const fetchDirectoryPlace = async (propertyCode, slug) => {
  if (!propertyCode || !slug) {
    throw new Error("Property code and place slug are required.");
  }

  const response = await api.get(
    `/directory/${encodeURIComponent(propertyCode)}/places/${encodeURIComponent(slug)}/`
  );
  return response.data;
};
